"use client";

/**
 * The ONE upload state for the files surface (founder 2026-10-01: "Upload
 * doesnt show anything during the time of uploading").
 *
 * Every entry point (the Files room's Upload pill, a drop or paste anywhere,
 * the folder toolbar's Upload, the phone's More actions menu) feeds this store
 * through `useUploadFiles`, and the one tray (FilesUploadTray) reads it. It
 * replaces a TanStack mutation per component, whose `isPending` only the
 * component that started the upload could see: the toolbar's upload showed
 * nothing but a disabled icon.
 *
 * Why a store and not `mutationKey` + `useMutationState`: the tray needs a row
 * per FILE with bytes-sent progress and a cancel, and a mutation's state holds
 * neither (it is one batch, with no progress). Progress would have needed a
 * second store beside the mutation cache, which is two sources of truth for
 * one upload. Server data (the listing) stays in React Query.
 */

import { create } from "zustand";

/**
 * waiting   → picked, behind the files ahead of it (uploads go one at a time)
 * uploading → its bytes are going up (`progress` 0..1)
 * finishing → the bytes are up; the server is filing it
 * done      → in Files
 * failed    → refused or broken (`error` says why, in plain words)
 */
export type UploadPhase =
  "waiting" | "uploading" | "finishing" | "done" | "failed";

export interface UploadRow {
  id: string;
  filename: string;
  sizeBytes: number;
  phase: UploadPhase;
  /** Bytes sent, 0..1, while `uploading`. */
  progress?: number;
  /** Why it failed, one line that names the file. */
  error?: string;
}

/** How long a finished batch stays in the tray before it clears itself. */
export const DONE_LINGER_MS = 3000;

export const isActive = (row: UploadRow): boolean =>
  row.phase === "waiting" ||
  row.phase === "uploading" ||
  row.phase === "finishing";

/** A row can be cancelled while its bytes have not all gone up. */
export const isCancellable = (row: UploadRow): boolean =>
  row.phase === "waiting" || row.phase === "uploading";

/**
 * One cancel per row still waiting or uploading. Held outside the state:
 * nothing renders it, and an AbortController is not data.
 */
const cancels = new Map<string, AbortController>();

/** Arm a row's cancel; the signal its PUT and server calls ride. */
export function armCancel(id: string): AbortSignal {
  const ctrl = new AbortController();
  cancels.set(id, ctrl);
  return ctrl.signal;
}

/** The bytes are up (or the row settled): nothing left to cancel. */
export function releaseCancel(id: string): void {
  cancels.delete(id);
}

let seq = 0;
export const nextUploadId = (): string => `upload-${++seq}`;

interface UploadState {
  rows: UploadRow[];
  /**
   * Whose uploads these are. The store lives as long as the tab, and signing
   * out does not reload the page, so the next person to sign in must never see
   * (or keep running) the last person's batch.
   */
  ownerId: string | null;
  /**
   * Make this person the owner: another person's rows are aborted and cleared.
   * A store nobody owns yet (a fresh tab) just takes the owner.
   */
  claim: (ownerId: string) => void;
  add: (rows: UploadRow[]) => void;
  patch: (id: string, change: Partial<Omit<UploadRow, "id">>) => void;
  /** Stop a waiting or uploading file: its PUT aborts and its row goes. */
  cancel: (id: string) => void;
  /** Drop one settled row (a failure the person has read). */
  dismiss: (id: string) => void;
  /** Drop the finished rows (the tray's own clock, once nothing is moving). */
  clearDone: () => void;
  /** Drop every settled row (the tray's close). */
  clearSettled: () => void;
}

export const useUploadStore = create<UploadState>()((set) => ({
  rows: [],
  ownerId: null,

  claim: (ownerId) =>
    set((s) => {
      if (s.ownerId === ownerId) return s;
      if (s.ownerId === null) return { ownerId };
      for (const ctrl of cancels.values()) ctrl.abort();
      cancels.clear();
      return { ownerId, rows: [] };
    }),

  add: (rows) => set((s) => ({ rows: [...s.rows, ...rows] })),

  patch: (id, change) =>
    set((s) => ({
      rows: s.rows.map((r) => (r.id === id ? { ...r, ...change } : r)),
    })),

  cancel: (id) => {
    cancels.get(id)?.abort();
    cancels.delete(id);
    set((s) => ({ rows: s.rows.filter((r) => r.id !== id) }));
  },

  dismiss: (id) => set((s) => ({ rows: s.rows.filter((r) => r.id !== id) })),

  clearDone: () =>
    set((s) => ({ rows: s.rows.filter((r) => r.phase !== "done") })),

  clearSettled: () => set((s) => ({ rows: s.rows.filter(isActive) })),
}));
